'use client';

import * as React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { X, MessageSquarePlus } from 'lucide-react';
import { ScrollArea } from '@sourcepilot/ui/framework/ui/atoms/scroll-area';
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button';
import {
  miniAppWidthAtom,
  activeMiniAppAtom,
  useTabData,
} from '@sourcepilot/ui/framework/state/session-data';
import { useAppHost } from '@sourcepilot/ui/framework/state/AppHostContext';

const MIN_WIDTH = 100;
const MAX_WIDTH = 600;

/**
 * MiniAppArea — the shell-side container that hosts the currently-active
 * mini-app component, with a draggable resize handle and a header showing
 * the workspace title.
 *
 * All domain-coupled logic — fetching the workspace, subscribing to
 * GraphStore node-created events for the workspace's contents, refreshing
 * on patches — lives in the host app (`SourcePilotAppHostProvider` for
 * sourcepilot). ui only consumes the derived `workspaceNameAtom` and the
 * `miniAppRegistry`, plus the generic shell-state atoms (active mini-app,
 * width).
 */
export function MiniAppArea() {
  const [miniAppWidth, setMiniAppWidth] = useAtom(miniAppWidthAtom);
  const [activeMiniApp, setActiveMiniApp] = useAtom(activeMiniAppAtom);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartX, setDragStartX] = React.useState(0);
  const [dragStartWidth, setDragStartWidth] = React.useState(miniAppWidth);

  const { miniAppRegistry, workspaceNameAtom } = useAppHost();
  const workspaceName = useAtomValue(workspaceNameAtom);
  const { navigateToTab } = useTabData();

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStartX(e.clientX - 1);
      setDragStartWidth(miniAppWidth);
    },
    [miniAppWidth],
  );

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStartX;
        const newWidth = Math.max(MIN_WIDTH, Math.min(dragStartWidth + deltaX, MAX_WIDTH));
        setMiniAppWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, setMiniAppWidth, dragStartX, dragStartWidth]);

  const renderMiniAppContent = () => {
    if (!activeMiniApp) return null;
    const Component = miniAppRegistry[activeMiniApp];
    return Component ? <Component containerWidth={miniAppWidth} /> : null;
  };

  if (!activeMiniApp) return null;

  return (
    <div
      className="relative h-[calc(100vh-45px)] flex flex-col"
      style={{ width: `${miniAppWidth}px`, minWidth: `${MIN_WIDTH}px`, flexShrink: 0 }}
    >
      {/* title bar */}
      <div className="flex items-center justify-between h-[34px] shrink-0 bg-gray-100">
        <div className="truncate ml-2 font-semibold text-gray-600 text-sm">
          {workspaceName || 'loading workspace...'}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              navigateToTab({
                id: 'new',
                title: 'New Chat',
                contentType: 'chat',
              });
            }}
          >
            <MessageSquarePlus className="h-4 w-4 text-black hover:text-[#00ccaa]" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 shrink-0"
            onClick={() => setActiveMiniApp(null)}
          >
            <X className="h-4 w-4 text-[#00ccaa]" />
          </Button>
        </div>
      </div>

      {/* main content */}
      <ScrollArea className="flex-1">{renderMiniAppContent()}</ScrollArea>

      {/* resize bar */}
      <div
        className="absolute -right-[1px] top-0 bottom-0 w-[1px] bg-gray-200 cursor-col-resize transition-all"
        onMouseDown={handleMouseDown}
      >
        <div className="bg-[#2f4858] absolute inset-y-0 w-[10px] opacity-0 group-hover:opacity-100 transition-opacity z-50" />
      </div>
    </div>
  );
}
